import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "./ui/button";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/gallery", label: "Gallery" },
    { path: "/about", label: "About" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "glass py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="w-10 h-10 relative">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <defs>
                <linearGradient id="copperGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#B87333" />
                  <stop offset="50%" stopColor="#D8AB4E" />
                  <stop offset="100%" stopColor="#8B4513" />
                </linearGradient>
              </defs>
              <polygon points="50,5 95,85 5,85" fill="none" stroke="url(#copperGradient)" strokeWidth="3" />
              <polygon points="50,25 75,70 25,70" fill="none" stroke="url(#copperGradient)" strokeWidth="2" />
            </svg>
          </div>
          <span className="font-heading text-xl tracking-wider copper-text">KAIROS</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`font-body text-sm tracking-widest uppercase transition-colors duration-300 ${
                isActive(link.path)
                  ? "text-copper"
                  : "text-gray-400 hover:text-copper"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* CTA Button */}
        <div className="hidden md:block">
          <Link to="/quote">
            <Button
              data-testid="get-quote-btn"
              className="bg-copper hover:bg-copper-light text-white font-body text-sm tracking-widest uppercase px-6 py-2 transition-all duration-300 hover:shadow-[0_0_15px_rgba(184,115,51,0.4)]"
            >
              Get Quote
            </Button>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          data-testid="mobile-menu-btn"
          className="md:hidden text-gray-300 hover:text-copper transition-colors"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden glass mt-4 mx-6 p-6 animate-fade-in"
        >
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                className={`font-body text-sm tracking-widest uppercase py-2 transition-colors duration-300 ${
                  isActive(link.path)
                    ? "text-copper"
                    : "text-gray-400 hover:text-copper"
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/quote" onClick={() => setIsMobileMenuOpen(false)}>
              <Button
                data-testid="mobile-get-quote-btn"
                className="w-full bg-copper hover:bg-copper-light text-white font-body text-sm tracking-widest uppercase mt-4"
              >
                Get Quote
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
